/* Country / language pickers (header dropdowns and side menu): put the chosen value in the form and submit it. */
(() => {
  if (window.c4pLocalizationReady) return;
  window.c4pLocalizationReady = true;

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-c4p-locale-value]');
    if (!option) return;

    const form = option.closest('form');
    const field = form && form.querySelector(`input[name="${option.getAttribute('data-c4p-locale-field')}"]`);
    if (!field) return;

    event.preventDefault();
    field.value = option.getAttribute('data-c4p-locale-value');
    option.setAttribute('aria-busy', 'true');
    form.submit();
  });
})();

/* Cars4Part header: sticky states, promo bar, dropdowns, announcement rotation and cart count. */
(() => {
  if (window.customElements.get('c4p-header')) return;

  const desktopQuery = window.matchMedia('(min-width: 1000px)');

  class C4pHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.announcement = this.querySelector('[data-c4p-announcement]');
      this.bar = this.querySelector('[data-c4p-bar]');
      this.mainWrap = this.querySelector('[data-c4p-main-wrap]');
      this.main = this.querySelector('[data-c4p-main]');
      this.promo = this.querySelector('[data-c4p-promo]');
      this.countEls = Array.from(this.querySelectorAll('[data-c4p-cart-count]'));
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.lastY = window.scrollY;
      this.measure();
      this.resizeObserver = new ResizeObserver(() => this.measure());
      [this.announcement, this.promo, this.bar, this.main, this].forEach((el) => el && this.resizeObserver.observe(el));

      if (this.hasAttribute('sticky')) {
        let ticking = false;
        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.onScroll();
          });
        }, { passive: true, signal });
        this.onScroll(true);
      }

      desktopQuery.addEventListener('change', () => {
        this.classList.remove('is-stuck', 'is-unsticking', 'is-fixed', 'is-fixed-animate');
        this.measure();
        this.onScroll(true);
      }, { signal });

      this.promo?.querySelector('[data-c4p-promo-close]')?.addEventListener('click', () => this.closePromo(), { signal });
      this.setupDropdowns(signal);
      this.startAnnouncement();

      document.addEventListener('cart:change', (event) => {
        const cart = event.detail && event.detail.cart;
        if (cart && typeof cart.item_count === 'number') this.setCount(cart.item_count);
      }, { signal });

      document.addEventListener('cart:refresh', async () => {
        const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        this.setCount(cart.item_count);
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      clearInterval(this.annTimer);
    }

    get isDesktop() {
      return desktopQuery.matches;
    }

    measure() {
      if (this.isDesktop) {
        this.annHeight = 0;
        if (this.main && this.mainWrap && !this.classList.contains('is-fixed')) {
          this.mainHeight = Math.round(this.main.offsetHeight);
          this.mainWrap.style.setProperty('--c4p-main-h', `${this.mainHeight}px`);
        }
        document.documentElement.style.setProperty('--header-height', `${this.mainHeight || 0}px`);
      } else {
        // Everything above the mobile bar (promo + announcement) scrolls away
        this.annHeight = Math.round((this.announcement ? this.announcement.offsetHeight : 0) + (this.promo ? this.promo.offsetHeight : 0));
        if (this.bar) document.documentElement.style.setProperty('--header-height', `${Math.round(this.bar.offsetHeight)}px`);
      }

      this.section.style.setProperty('--c4p-ann-height', `${this.annHeight}px`);
      document.documentElement.style.setProperty('--announcement-bar-height', `${this.annHeight}px`);
    }

    onScroll(initial = false) {
      if (!this.hasAttribute('sticky')) return;
      if (this.isDesktop) this.updateFixed(initial);
      else this.updateStuck(initial);
      this.lastY = window.scrollY;
    }

    /* Mobile: the whole bar is sticky, add a compact/shadow state once scrolled */
    updateStuck(initial) {
      const y = window.scrollY;
      const stuck = this.classList.contains('is-stuck');

      if (!stuck && y > this.annHeight + 24) {
        this.classList.remove('is-unsticking');
        this.classList.add('is-stuck');
      } else if (stuck && y <= this.annHeight + 4) {
        this.classList.remove('is-stuck');
        if (!initial) {
          this.classList.add('is-unsticking');
          clearTimeout(this.unstickTimer);
          this.unstickTimer = setTimeout(() => this.classList.remove('is-unsticking'), 600);
        }
      }
    }

    /*
     * Desktop: once the whole header has scrolled away, the main row slides down as a fixed bar. Scrolling back up,
     * it is released exactly where the main row sits in the page, so the swap is seamless.
     */
    updateFixed(initial) {
      if (!this.main || !this.mainWrap) return;
      const y = window.scrollY;
      const sectionTop = this.section.getBoundingClientRect().top + y;
      const mainTop = this.mainWrap.getBoundingClientRect().top + y;
      const headerBottom = sectionTop + this.section.offsetHeight;
      const fixed = this.classList.contains('is-fixed');

      if (!fixed && y > headerBottom) {
        this.classList.add('is-fixed');
        this.classList.toggle('is-fixed-animate', !initial);
      } else if (fixed && y <= mainTop) {
        this.classList.remove('is-fixed', 'is-fixed-animate');
      }
    }

    closePromo() {
      if (!this.promo) return;
      this.promo.classList.add('is-dismissed');
      try {
        sessionStorage.setItem('c4p_promo_closed', this.promo.getAttribute('data-key'));
      } catch (e) { /* storage unavailable */ }
    }

    setupDropdowns(signal) {
      this.dropdowns = Array.from(this.querySelectorAll('c4p-dropdown'));

      this.dropdowns.forEach((dropdown) => {
        const toggle = dropdown.querySelector('.c4p-dropdown__toggle');
        const panel = dropdown.querySelector('.c4p-dropdown__panel');
        if (!toggle || !panel) return;

        toggle.addEventListener('click', () => {
          const open = !dropdown.classList.contains('is-open');
          this.closeDropdowns();
          if (open) this.openDropdown(dropdown);
        }, { signal });

        const filter = panel.querySelector('[data-c4p-filter]');
        filter?.addEventListener('input', () => {
          const term = filter.value.trim().toLowerCase();
          panel.querySelectorAll('[data-name]').forEach((option) => {
            option.hidden = term !== '' && !option.getAttribute('data-name').includes(term);
          });
        }, { signal });
      });

      document.addEventListener('click', (event) => {
        if (!event.target.closest('c4p-dropdown')) this.closeDropdowns();
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeDropdowns();
      }, { signal });
    }

    openDropdown(dropdown) {
      const panel = dropdown.querySelector('.c4p-dropdown__panel');
      panel.hidden = false;
      requestAnimationFrame(() => dropdown.classList.add('is-open'));
      dropdown.querySelector('.c4p-dropdown__toggle').setAttribute('aria-expanded', 'true');
      const current = panel.querySelector('[aria-current="true"]');
      if (current) panel.scrollTop = current.offsetTop - panel.clientHeight / 2;
    }

    closeDropdowns() {
      (this.dropdowns || []).forEach((dropdown) => {
        if (!dropdown.classList.contains('is-open')) return;
        dropdown.classList.remove('is-open');
        dropdown.querySelector('.c4p-dropdown__toggle').setAttribute('aria-expanded', 'false');
        const panel = dropdown.querySelector('.c4p-dropdown__panel');
        setTimeout(() => {
          if (!dropdown.classList.contains('is-open')) panel.hidden = true;
        }, 250);
      });
    }

    startAnnouncement() {
      if (!this.announcement || !this.announcement.hasAttribute('data-autoplay')) return;
      const messages = Array.from(this.announcement.querySelectorAll('.c4p-announcement__message'));
      if (messages.length < 2) return;

      const delay = parseInt(this.announcement.getAttribute('data-autoplay'), 10) * 1000;
      let index = 0;

      this.annTimer = setInterval(() => {
        if (document.hidden) return;
        const current = messages[index];
        index = (index + 1) % messages.length;
        const next = messages[index];

        current.classList.add('is-leaving');
        current.classList.remove('is-active');
        next.classList.remove('is-leaving');
        next.classList.add('is-active');
        setTimeout(() => current.classList.remove('is-leaving'), 600);
      }, delay);
    }

    setCount(count) {
      this.countEls.forEach((el) => {
        const previous = parseInt(el.textContent, 10) || 0;
        el.textContent = count;
        if (!el.hasAttribute('data-always-visible')) el.classList.toggle('is-visible', count > 0);

        if (count > previous) {
          el.classList.remove('is-bumped');
          void el.offsetWidth;
          el.classList.add('is-bumped');
        }
      });
    }
  }

  window.customElements.define('c4p-header', C4pHeader);
})();
